import { ITokenStorage, SDK } from "@axl-dev/school-sdk";
import { createContext, FC, PropsWithChildren, useEffect, useMemo } from "react";

import { useSettings } from "@app/shared/lib/hooks";

import { CookieTokenStorage } from "./token-storage";

interface SdkContextType {
  sdk: SDK;
  tokenStorage: ITokenStorage;
}

export const SdkContext = createContext({} as SdkContextType);

export const SdkProvider: FC<PropsWithChildren> = ({ children }) => {
  const settings = useSettings();
  const tokenStorage = useMemo<ITokenStorage>(() => new CookieTokenStorage(), []);

  
  const sdk = useMemo(
    () => ({
      tokenStorage,
      sdk: new SDK(tokenStorage, {
        schoolDomain:
          import.meta.env.VITE_APP_SCHOOL_DOMAIN === "ORIGIN"
            ? window.location.origin
            : import.meta.env.VITE_APP_SCHOOL_DOMAIN,
        chat: {
          apiUrl: settings.chat.apiUrl,
          clientConfig: {
            host: settings.chat.hubUrl,
            invokeTimeoutMs: 10000,
          },
        },
        hub: {
          clientConfig: {
            host: settings.hub.url,
            invokeTimeoutMs: 10000,
          },
        },
      }),
    }),
    [settings.chat.apiUrl, settings.chat.hubUrl, settings.hub.url, tokenStorage],
  );

  const refreshIntervalMs = useMemo(() => {
    const mins = Number(settings.auth?.tokenUpdateIntervalInMins);
    return (Number.isFinite(mins) && mins > 0 ? mins : 30) * 60 * 1000;
  }, [settings.auth?.tokenUpdateIntervalInMins]);

  useEffect(() => {
    const refresh = async () => {
      const tokens = await tokenStorage.getTokens();
      if (!tokens?.refreshToken) return;
      const result = await sdk.sdk.api.auth
        .refreshToken(tokens.refreshToken)
        .catch(() => null);
      if (result?.success && result.body) await tokenStorage.setTokens(result.body);
    };

    refresh();
    const intervalId = window.setInterval(refresh, refreshIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [sdk, tokenStorage, refreshIntervalMs]);

  return <SdkContext.Provider value={sdk}>{children}</SdkContext.Provider>;
};
